import React, { useState, useEffect } from 'react';
import MetalsTracker from './pages/MetalsTracker';
import EdgeTerminal from './pages/EdgeTerminal';
import Intelligence from './pages/Intelligence';
import ETFXRay from './pages/ETFXRay';
import PeerPulse from './pages/PeerPulse';
import ComparableScreener from './pages/ComparableScreener';
import NewsFeed from './pages/News';
import { AlertCircle, TrendingUp, DollarSign, Activity, Bell, BarChart3, Settings, Menu, ChevronDown, ChevronUp, Calendar, Plus, Trash2, LogOut, X, Edit, PieChart, AlertTriangle, Info, CheckCircle, TrendingDown, Percent, Shield, Layers, Search, Newspaper } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  'https://gpthlfzfevswajqkhedp.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImdwdGhsZnpmZXZzd2FqcWtoZWRwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjEwMzUzMzIsImV4cCI6MjA3NjYxMTMzMn0.BQFJOXHPhehUS4jIPkpqK7lGDQKyIEjwMzv4lTh9GcA'
);

const WORKER_URL = 'https://tickertapapi.avetter90.workers.dev';

const POSITION_TYPE_COLORS = {
  'Income Engine': '#22c55e',    // Green - cash flow generators
  'Wealth Core': '#f59e0b',      // Amber - core holdings
  'Volatility Engine': '#8b5cf6', // Purple - vol plays
  'Hard Asset': '#94a3b8',       // Silver/slate - physical assets
  'Other': '#64748b',            // Gray
};

// ─── ALERT ENGINE ────────────────────────────────────────────────────────────
function generateAlerts(positions) {
  const alerts = [];
  positions.forEach((p) => {
    const ticker = p.ticker;
    const yield_ = p.currentYield || 0;
    const payout = p.payoutCoverage || 0;
    const nextExDate = p.nextExDate ? new Date(p.nextExDate) : null;
    const today = new Date();
    const daysToEx = nextExDate ? Math.ceil((nextExDate - today) / (1000 * 60 * 60 * 24)) : null;
    const annualDiv = p.annualDividend || 0;
    const history = p.distributionHistory || [];

    if (payout > 100) alerts.push({ tier: 1, ticker, message: `Payout coverage ${payout}% — distribution may be unsustainable`, action: 'Review position sizing. Consider reducing exposure.', timestamp: new Date() });
    if (yield_ > 0 && history.length >= 2) {
      const latest = history[history.length - 1];
      const prev = history[history.length - 2];
      if (prev > 0 && (prev - latest) / prev > 0.2) alerts.push({ tier: 1, ticker, message: `Dividend cut detected — distribution dropped ${(((prev - latest) / prev) * 100).toFixed(1)}%`, action: 'High priority review. Assess whether thesis still holds.', timestamp: new Date() });
    }
    if (payout >= 80 && payout <= 100) alerts.push({ tier: 2, ticker, message: `Payout coverage ${payout}% — elevated but not critical`, action: 'Monitor next earnings report closely.', timestamp: new Date() });
    if (daysToEx !== null && daysToEx >= 0 && daysToEx <= 7) alerts.push({ tier: 2, ticker, message: `Ex-dividend date in ${daysToEx} day${daysToEx === 1 ? '' : 's'} — ${nextExDate.toLocaleDateString()}`, action: 'Ensure shares held before ex-date to capture distribution.', timestamp: new Date() });
    if (yield_ > 0 && history.length >= 3) {
      const recent = history.slice(-3);
      if (recent[0] > recent[1] && recent[1] > recent[2]) alerts.push({ tier: 2, ticker, message: `Distribution trending down 3 consecutive periods`, action: 'Watch for further deterioration.', timestamp: new Date() });
    }
    if (daysToEx !== null && daysToEx > 7 && daysToEx <= 30) alerts.push({ tier: 3, ticker, message: `Ex-dividend date in ${daysToEx} days — ${nextExDate.toLocaleDateString()}`, action: 'No action needed. Heads up.', timestamp: new Date() });
    if (annualDiv > 0 && yield_ > 8) alerts.push({ tier: 3, ticker, message: `High yield position — ${yield_.toFixed(2)}% current yield`, action: 'Confirm yield is supported by fundamentals, not price decay.', timestamp: new Date() });
  });
  return alerts.sort((a, b) => a.tier - b.tier);
}

const TickerTap = () => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authView, setAuthView] = useState('signin');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [shimmerGain, setShimmerGain] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalDay, setSelectedCalDay] = useState(null);
  const [calendarSearch, setCalendarSearch] = useState('');
  const [calendarSort, setCalendarSort] = useState('yours'); // 'yours' | 'alpha' | 'type'
  const [calendarPopupSearch, setCalendarPopupSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [positions, setPositions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [serverAlerts, setServerAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [marketQuotes, setMarketQuotes] = useState([]);
  const [portfolio, setPortfolio] = useState({ totalValue: 0, totalCost: 0, monthlyIncome: 0, incomeReliability: 0 });
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [subscription, setSubscription] = useState({ tier: 'free', max_tickers: 5, active: true });
  const [stripeLoading, setStripeLoading] = useState(false);

  const SP500_TOP30 = ['AAPL','MSFT','NVDA','AMZN','GOOGL','META','BRK-B','LLY','AVGO','JPM','TSLA','UNH','V','XOM','MA','COST','HD','PG','JNJ','ABBV','BAC','MRK','ORCL','CVX','KO','NFLX','AMD','CRM','PEP','TMO'];
  const [formData, setFormData] = useState({ ticker: '', shares: '', cost_basis: '', position_type: 'Income Engine', notes: '' });
  const [editingPosition, setEditingPosition] = useState(null);
  const [selectedPositionForOption, setSelectedPositionForOption] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session) {
        fetchPortfolio(session);
        fetchServerAlerts();
        fetchSubscription(session);
      }
    });
    fetchMarketQuotes();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session) {
        fetchPortfolio(session);
        fetchServerAlerts();
        fetchSubscription(session);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Stripe redirect back to app
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      // Refresh subscription status after successful upgrade
      setTimeout(() => fetchSubscription(), 1500);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchMarketQuotes = async () => {
    try {
      const symbols = SP500_TOP30.join(',');
      const response = await fetch(`${WORKER_URL}/api/quotes?symbols=${symbols}`);
      if (!response.ok) return;
      const data = await response.json();
      const quotes = SP500_TOP30.map(symbol => ({ ticker: symbol, price: data[symbol]?.price || 0, change: data[symbol]?.change || 0 })).filter(q => q.price > 0);
      setMarketQuotes(quotes);
    } catch (error) { console.error('Error fetching market quotes:', error); }
  };

  const fetchPortfolio = async (currentSession) => {
    try {
      setRefreshing(true);
      const { data: { session: s } } = await supabase.auth.getSession();
      const activeSession = currentSession || s;
      if (!activeSession) return;
      await fetch(`${WORKER_URL}/portfolio/cache`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${activeSession.access_token}` } });
      const response = await fetch(`${WORKER_URL}/portfolio`, { headers: { 'Authorization': `Bearer ${activeSession.access_token}` } });
      if (!response.ok) throw new Error('Failed to fetch portfolio');
      const data = await response.json();
      if (data.positions) {
        const transformed = data.positions.map((p) => ({
          id: p.id, ticker: p.ticker, name: p.ticker, shares: p.shares, price: p.price || p.current_price || 0,
          costBasis: p.cost_basis, sector: p.position_type || 'Other',
          dayChange: p.day_change || 0, dayChangePct: parseFloat(p.day_change_pct) || 0,
          monthlyIncome: ((p.annualDividend || 0) / 12) * p.shares,
          annualDividend: p.annualDividend || 0, currentYield: p.dividendYield || 0,
          yieldOnCost: p.cost_basis > 0 ? ((p.annualDividend || 0) / p.cost_basis * 100) : 0,
          payoutCoverage: p.payoutCoverage || 0, distributionHistory: p.dividendHistory || [],
          nextExDate: p.nextExDate || null, nextPayDate: p.nextPayDate || null,
          week52High: p.week52High || null, week52Low: p.week52Low || null,
          health: p.payoutCoverage ? (p.payoutCoverage >= 100 ? 90 : p.payoutCoverage >= 75 ? 75 : p.payoutCoverage >= 60 ? 60 : 40) : 85,
          notes: p.notes || ''
        }));
        setPositions(transformed);
        setAlerts(generateAlerts(transformed));
        setDismissedAlerts(new Set());
        setLastRefreshed(new Date());
        // Refresh subscription tier after portfolio loads (ensures session is valid)
        fetchSubscription(activeSession);
      }
    } catch (error) { console.error('Error fetching portfolio:', error); }
    finally { setRefreshing(false); }
  };

  // Fetch server-side signal alerts from v_pending_alerts
  const fetchServerAlerts = async () => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) return;
      const response = await fetch(`${WORKER_URL}/api/signal-alerts`, {
        headers: { 'Authorization': `Bearer ${s.access_token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.alerts) {
        // Map server alerts to match client alert structure
        const mapped = data.alerts.map(a => ({
          id: a.alert_id,
          signal_id: a.signal_id,
          tier: a.severity_tier === 'CRITICAL' ? 1 : a.severity_tier === 'HIGH' ? 2 : 3,
          ticker: a.ticker,
          message: a.signal_message || a.message,
          action: a.action_hint || 'Review position.',
          timestamp: new Date(a.created_at),
          signal_code: a.signal_code,
          category: a.category,
          raw_value: a.raw_value,
          threshold: a.threshold,
          isServer: true // Flag to distinguish from client-generated alerts
        }));
        setServerAlerts(mapped);
      }
    } catch (error) { console.error('Error fetching server alerts:', error); }
  };

  // Acknowledge a server alert
  const acknowledgeAlert = async (alertId) => {
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) return;
      const response = await fetch(`${WORKER_URL}/api/signal-alerts/${alertId}/acknowledge`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${s.access_token}` }
      });
      if (response.ok) {
        setServerAlerts(prev => prev.filter(a => a.id !== alertId));
      }
    } catch (error) { console.error('Error acknowledging alert:', error); }
  };

  // Fetch subscription tier from worker
  const fetchSubscription = async (currentSession) => {
    try {
      const s = currentSession || (await supabase.auth.getSession()).data.session;
      if (!s) return;
      const resp = await fetch(`${WORKER_URL}/stripe/subscription`, {
        headers: { 'Authorization': `Bearer ${s.access_token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setSubscription({ tier: data.tier || 'free', max_tickers: data.max_tickers || 5, active: data.active !== false });
      }
    } catch (err) { console.error('Error fetching subscription:', err); }
  };

  // Redirect to Stripe Checkout for Pro upgrade
  const handleUpgrade = async () => {
    setStripeLoading(true);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) return;
      const resp = await fetch(`${WORKER_URL}/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${s.access_token}` }
      });
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to start checkout. Please try again.');
      }
    } catch (err) {
      alert('Something went wrong. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  // Redirect to Stripe Customer Portal for subscription management
  const handleManageSubscription = async () => {
    setStripeLoading(true);
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) return;
      const resp = await fetch(`${WORKER_URL}/stripe/create-portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${s.access_token}` }
      });
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.error || 'Failed to open billing portal. Please try again.');
      }
    } catch (err) {
      alert('Something went wrong. Please try again.');
    } finally {
      setStripeLoading(false);
    }
  };

  // Fetch dividend and earnings calendar from FMP via worker
  const fetchCalendarEvents = async () => {
    try {
      // Get user's tickers for highlighting
      const userTickers = new Set(positions.map(p => p.ticker.toUpperCase()));
      
      // Fetch calendar data from worker (public endpoint, no auth needed)
      const response = await fetch(`${WORKER_URL}/api/calendar`);
      
      console.log('Calendar fetch status:', response.status);
      
      if (!response.ok) {
        console.error('Calendar fetch failed:', response.status);
        // If endpoint fails, fall back to position data only
        const positionEvents = positions.flatMap(p => {
          const events = [];
          if (p.nextExDate) {
            events.push({ 
              type: 'dividend', 
              ticker: p.ticker, 
              date: new Date(p.nextExDate), 
              label: 'Ex-Div',
              isUserPosition: true 
            });
          }
          if (p.nextPayDate) {
            events.push({ 
              type: 'payment', 
              ticker: p.ticker, 
              date: new Date(p.nextPayDate), 
              label: 'Pay Date',
              isUserPosition: true 
            });
          }
          return events;
        });
        setCalendarEvents(positionEvents);
        return;
      }
      
      const data = await response.json();
      console.log('Calendar data received:', data.dividends?.length, 'dividends,', data.earnings?.length, 'earnings');
      
      const events = [];

      // US exchange whitelist — NYSE, NASDAQ, AMEX, CBOE, BATS, user positions always pass
      const US_EXCHANGES = new Set(['NYSE','NASDAQ','AMEX','NYSE ARCA','NYSE MKT','CBOE','BATS','IEX','NYSEARCA','NYSEMKT','NYSE AMERICAN']);
      const isUsListed = (symbol, exchange, isUserPos) => {
        if (isUserPos) return true;
        if (!symbol || symbol.includes('.')) return false;
        // If exchange data present, trust it
        if (exchange) return US_EXCHANGES.has(exchange.toUpperCase().trim());
        // Fallback: block obvious OTC patterns only (F-suffix foreign, dots already caught above)
        if (/^[A-Z]{4,5}F$/.test(symbol)) return false; // AKRTF, ANFGF etc
        return true; // default allow if no exchange data
      };

      // Process dividend calendar
      if (data.dividends && Array.isArray(data.dividends)) {
        data.dividends.forEach(d => {
          const isUserPos = userTickers.has(d.symbol?.toUpperCase());
          if (d.symbol && d.date && isUsListed(d.symbol, d.exchange, isUserPos)) {
            events.push({
              type: 'dividend',
              ticker: d.symbol,
              date: d.date,
              label: 'Ex-Div',
              amount: d.dividend,
              isUserPosition: isUserPos
            });
            // Also add pay date as a separate event if present
            if (d.paymentDate) {
              events.push({
                type: 'payment',
                ticker: d.symbol,
                date: d.paymentDate,
                label: 'Pay Date',
                amount: d.dividend,
                isUserPosition: isUserPos
              });
            }
          }
        });
      }
      
      // Process earnings calendar
      if (data.earnings && Array.isArray(data.earnings)) {
        data.earnings.forEach(e => {
          const isUserPos = userTickers.has(e.symbol?.toUpperCase());
          if (e.symbol && e.date && isUsListed(e.symbol, e.exchange, isUserPos)) {
            events.push({
              type: 'earnings',
              ticker: e.symbol,
              date: e.date,
              label: e.time === 'bmo' ? 'Earnings AM' : e.time === 'amc' ? 'Earnings PM' : 'Earnings',
              isUserPosition: isUserPos
            });
          }
        });
      }
      
      console.log('Total events processed:', events.length);
      setCalendarEvents(events);
    } catch (error) { 
      console.error('Error fetching calendar:', error);
      // Fallback to position data
      const positionEvents = positions.flatMap(p => {
        const events = [];
        if (p.nextExDate) {
          events.push({ type: 'dividend', ticker: p.ticker, date: new Date(p.nextExDate), label: 'Ex-Div', isUserPosition: true });
        }
        if (p.nextPayDate) {
          events.push({ type: 'payment', ticker: p.ticker, date: new Date(p.nextPayDate), label: 'Pay Date', isUserPosition: true });
        }
        return events;
      });
      setCalendarEvents(positionEvents);
    }
  };

  useEffect(() => {
    if (positions.length === 0) return;
    const totalValue = positions.reduce((sum, pos) => sum + (pos.shares * pos.price), 0);
    const totalCost = positions.reduce((sum, pos) => sum + (pos.shares * pos.costBasis), 0);
    const monthlyIncome = positions.reduce((sum, pos) => sum + pos.monthlyIncome, 0);
    const weightedHealth = positions.reduce((sum, pos) => {
      const weight = (pos.shares * pos.price) / totalValue;
      return sum + (pos.health * weight);
    }, 0);
    setPortfolio({ totalValue, totalCost, monthlyIncome, incomeReliability: weightedHealth });
  }, [positions]);

  const handleSignUp = async (e) => { e.preventDefault(); const { error } = await supabase.auth.signUp({ email: e.target.email.value, password: e.target.password.value }); if (error) alert(error.message); else alert('Check your email for confirmation!'); };
  const handleSignIn = async (e) => { e.preventDefault(); const { error } = await supabase.auth.signInWithPassword({ email: e.target.email.value, password: e.target.password.value }); if (error) alert(error.message); };
  const handleSignOut = async () => { await supabase.auth.signOut(); setPositions([]); setAlerts([]); };

  const handleAddPosition = async (e) => {
    e.preventDefault();
    // Gate: check position limit before submitting
    if (positions.length >= subscription.max_tickers) {
      const isPro = subscription.tier === 'pro' || subscription.tier === 'premium' || subscription.tier === 'founder';
      if (!isPro) {
        setShowAddModal(false);
        if (window.confirm(`You've reached the ${subscription.max_tickers}-position limit on the Free plan.\n\nUpgrade to Pro for 20 positions + senator/insider alerts.\n\nUpgrade now?`)) {
          handleUpgrade();
        }
        return;
      }
    }
    try {
      const response = await fetch(`${WORKER_URL}/portfolio`, { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ ticker: formData.ticker.toUpperCase(), shares: parseFloat(formData.shares), cost_basis: parseFloat(formData.cost_basis), position_type: formData.position_type, notes: formData.notes }) });
      const data = await response.json();
      if (!response.ok) { alert(data.error || 'Failed to add position'); return; }
      setFormData({ ticker: '', shares: '', cost_basis: '', position_type: 'Income Engine', notes: '' });
      setShowAddModal(false);
      await fetchPortfolio();
    } catch (error) { alert('Failed to add position: ' + error.message); }
  };

  const handleEditPosition = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${WORKER_URL}/portfolio/${editingPosition.id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ shares: parseFloat(formData.shares), cost_basis: parseFloat(formData.cost_basis), position_type: formData.position_type, notes: formData.notes }) });
      if (!response.ok) throw new Error('Failed to update position');
      setEditingPosition(null);
      setFormData({ ticker: '', shares: '', cost_basis: '', position_type: 'Income Engine', notes: '' });
      setShowEditModal(false);
      await fetchPortfolio();
    } catch (error) { alert('Failed to update position'); }
  };

  const handleDeletePosition = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      const response = await fetch(`${WORKER_URL}/portfolio/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (!response.ok) throw new Error('Failed to delete');
      await fetchPortfolio();
    } catch (error) { alert('Failed to delete position'); }
  };

  const startEdit = (position) => {
    setEditingPosition(position);
    setFormData({ ticker: position.ticker, shares: position.shares.toString(), cost_basis: position.costBasis.toString(), position_type: position.sector, notes: position.notes || '' });
    setShowEditModal(true);
  };

  useEffect(() => {
    expandedRows.forEach(ticker => {
      if (!window.TradingView) {
        const script = document.createElement('script');
        script.src = 'https://s3.tradingview.com/tv.js';
        script.async = true;
        script.onload = () => initChart(ticker);
        document.head.appendChild(script);
      } else { initChart(ticker); }
    });
    function initChart(ticker) {
      const containerId = `tradingview_${ticker}`;
      const container = document.getElementById(containerId);
      if (container && window.TradingView) {
        container.innerHTML = '';
        new window.TradingView.widget({ autosize: true, symbol: ticker, interval: 'D', timezone: 'Etc/UTC', theme: 'dark', style: '2', locale: 'en', toolbar_bg: '#0f0f0f', enable_publishing: false, hide_top_toolbar: true, hide_side_toolbar: true, hide_legend: true, save_image: false, container_id: containerId, backgroundColor: '#0f0f0f', gridColor: '#1a1a1a', allow_symbol_change: false, studies: [] });
      }
    }
  }, [expandedRows]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `@keyframes scroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}.animate-scroll{display:flex;animation:scroll 40s linear infinite}.animate-scroll:hover{animation-play-state:paused}@keyframes ping{75%,100%{transform:scale(2);opacity:0}}.animate-ping{animation:ping 1.2s cubic-bezier(0,0,0.2,1) infinite}.animate-spin{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}.animate-shimmer{animation:shimmer 1.5s ease-in-out}`;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const toggleRow = (ticker) => {
    setExpandedRows(prev => { const s = new Set(prev); s.has(ticker) ? s.delete(ticker) : s.add(ticker); return s; });
  };

  const dismissAlert = (index) => {
    setDismissedAlerts(prev => new Set([...prev, index]));
  };

  // Merge server alerts (priority) with client-generated alerts
  const allAlerts = [...serverAlerts, ...alerts.filter(a => !serverAlerts.some(sa => sa.ticker === a.ticker && sa.signal_code === a.signal_code))];
  const visibleAlerts = allAlerts.filter((a, i) => !a.isServer ? !dismissedAlerts.has(alerts.indexOf(a)) : true);
  const tier1Count = visibleAlerts.filter(a => a.tier === 1).length;
  const tier2Count = visibleAlerts.filter(a => a.tier === 2).length;
  const tier3Count = visibleAlerts.filter(a => a.tier === 3).length;

  // Portfolio computed stats
  const totalGainLoss = portfolio.totalValue - portfolio.totalCost;
  const totalGainLossPct = portfolio.totalCost > 0 ? (totalGainLoss / portfolio.totalCost) * 100 : 0;
  const annualIncome = portfolio.monthlyIncome * 12;
  const portfolioYield = portfolio.totalValue > 0 ? (annualIncome / portfolio.totalValue) * 100 : 0;

  // Next ex-date across all positions
  const nextExDateEntry = positions
    .filter(p => p.nextExDate)
    .map(p => ({ ticker: p.ticker, date: new Date(p.nextExDate) }))
    .filter(p => p.date >= new Date())
    .sort((a, b) => a.date - b.date)[0];
  const daysToNextEx = nextExDateEntry ? Math.ceil((nextExDateEntry.date - new Date()) / (1000 * 60 * 60 * 24)) : null;

  if (loading) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white"><div>Loading...</div></div>;

  if (!user) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white p-6">
      <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent mb-2">TickerTap</h1>
        <p className="text-xs text-gray-500 mb-8">Income Watchdog for Smart Investors</p>
        <div className="flex gap-2 mb-6">
          <button onClick={() => setAuthView('signin')} className={`flex-1 py-2 rounded transition-colors ${authView === 'signin' ? 'bg-[#2a2a2a] text-white' : 'bg-[#1a1a1a] text-gray-400 hover:text-white'}`}>Sign In</button>
          <button onClick={() => setAuthView('signup')} className={`flex-1 py-2 rounded transition-colors ${authView === 'signup' ? 'bg-[#2a2a2a] text-white' : 'bg-[#1a1a1a] text-gray-400 hover:text-white'}`}>Sign Up</button>
        </div>
        {authView === 'signin' ? (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div><label className="block text-sm text-gray-400 mb-2">Email</label><input type="email" name="email" required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
            <div><label className="block text-sm text-gray-400 mb-2">Password</label><input type="password" name="password" required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
            <button type="submit" className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white py-2 rounded transition-colors">Sign In</button>
          </form>
        ) : (
          <form onSubmit={handleSignUp} className="space-y-4">
            <div><label className="block text-sm text-gray-400 mb-2">Email</label><input type="email" name="email" required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
            <div><label className="block text-sm text-gray-400 mb-2">Password</label><input type="password" name="password" required minLength={6} className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
            <button type="submit" className="w-full bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white py-2 rounded transition-colors">Create Account</button>
            <p className="text-xs text-gray-500 text-center">Free tier: Track up to 5 positions</p>
          </form>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#0a0a0a] text-white overflow-hidden">

      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} transition-all duration-300 bg-[#0f0f0f] border-r border-[#1a1a1a] flex-shrink-0 overflow-hidden`}>
        <div className="p-6">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">TickerTap</h1>
          <p className="text-xs text-gray-500 mt-1">Income Watchdog</p>
        </div>
        <nav className="px-3">
          {[
            { id: 'dashboard', icon: <BarChart3 className="w-5 h-5" />, label: 'Dashboard' },
            { id: 'analytics', icon: <Activity className="w-5 h-5" />, label: 'Analytics' },
            { id: 'metals', icon: <span style={{fontSize:'16px'}}>🥈</span>, label: 'Metals' },
            { id: 'edge', icon: <TrendingDown className="w-5 h-5" />, label: 'Edge' },
            { id: 'news', icon: <Newspaper className="w-5 h-5" />, label: 'News Feed' },
            { id: 'intelligence', icon: <Shield className="w-5 h-5" />, label: 'Intelligence', pro: true },
            { id: 'screener', icon: <Search className="w-5 h-5" />, label: 'Screener', pro: true },
            { id: 'etfxray', icon: <Layers className="w-5 h-5" />, label: 'ETF Lookthrough', comingSoon: true },
            { id: 'peerpulse', icon: <Activity className="w-5 h-5" />, label: 'Peer Pulse', pro: true },
          ].map(item => (
            <button key={item.id} onClick={() => setActiveView(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${activeView === item.id ? 'bg-blue-600/10 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}>
              {item.icon}<span className="text-sm font-medium">{item.label}</span>
              {item.pro && subscription.tier === 'free' && <span className="ml-auto text-xs bg-cyan-500/10 text-cyan-500 border border-cyan-500/20 px-1.5 py-0.5 rounded font-semibold">PRO</span>}

            </button>
          ))}
          <button onClick={() => setActiveView('alerts')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${activeView === 'alerts' ? 'bg-blue-600/10 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}>
            <div className="relative">
              <Bell className="w-5 h-5" />
              {(tier1Count + tier2Count) > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />}
            </div>
            <span className="text-sm font-medium">Alerts</span>
            {(tier1Count + tier2Count) > 0 && <span className="ml-auto text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold">{tier1Count + tier2Count}</span>}
          </button>
          <button onClick={() => setActiveView('settings')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${activeView === 'settings' ? 'bg-blue-600/10 text-blue-400' : 'text-gray-400 hover:bg-[#1a1a1a]'}`}>
            <Settings className="w-5 h-5" /><span className="text-sm font-medium">Settings</span>
          </button>
          <div className="mt-6 pt-6 border-t border-[#1a1a1a]">
            <div className="px-4 py-2 text-xs text-gray-500 truncate">{user.email}</div>
            <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#1a1a1a] transition-colors">
              <LogOut className="w-5 h-5" /><span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Ticker Scroll */}
        <div className="relative overflow-hidden bg-[#0f0f0f] border-b border-[#1a1a1a]">
          <div className="flex animate-scroll">
            {(marketQuotes.length > 0 ? [...marketQuotes, ...marketQuotes] : SP500_TOP30.map(t => ({ ticker: t, price: 0, change: 0 }))).map((q, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2.5 border-r border-[#1a1a1a] whitespace-nowrap">
                <span className="text-gray-400 font-semibold text-sm">{q.ticker}</span>
                {q.price > 0 && (<><span className="text-white font-bold text-sm">${q.price.toFixed(2)}</span><span className={`text-xs font-semibold ${q.change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{q.change >= 0 ? '+' : ''}{q.change.toFixed(2)}%</span></>)}
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="h-16 border-b border-[#1a1a1a] flex items-center justify-between px-8">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white"><Menu className="w-5 h-5" /></button>
          <div className="flex items-center gap-4">

            {/* Live indicator + subscription tier */}
            <button onClick={() => setActiveView('settings')} className="flex items-center gap-2 px-3 py-1.5 bg-[#0f0f0f] border border-[#1a1a1a] rounded-full hover:border-[#2a2a2a] transition-colors">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <span className="text-xs text-emerald-400 font-semibold tracking-wider">LIVE</span>
              <span className="h-3 w-px bg-[#2a2a2a]" />
              {subscription.tier === 'founder' ? (
                <span className="text-xs text-amber-400 font-bold tracking-wider">FOUNDER</span>
              ) : subscription.tier === 'pro' || subscription.tier === 'premium' ? (
                <span className="text-xs text-cyan-400 font-bold tracking-wider">PRO</span>
              ) : (
                <span className="text-xs text-gray-500 font-semibold tracking-wider">FREE</span>
              )}
            </button>

            {/* Divider */}
            <div className="h-6 w-px bg-[#1a1a1a]" />

            {/* Portfolio Value */}
            <div className="text-right">
              <div className="text-xs text-gray-600 uppercase tracking-wider">Portfolio Value</div>
              <div className="text-base font-bold text-white">${portfolio.totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-[#1a1a1a]" />

            {/* Monthly Income */}
            <div className="text-right">
              <div className="text-xs text-gray-600 uppercase tracking-wider">Monthly Income</div>
              <div className="text-base font-bold text-emerald-400">${portfolio.monthlyIncome.toFixed(0)}</div>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-[#1a1a1a]" />

            {/* Refresh */}
            <button onClick={() => fetchPortfolio()} disabled={refreshing} className="flex items-center gap-2 text-xs text-gray-500 hover:text-white disabled:opacity-50 transition-colors uppercase tracking-wider">
              <Activity className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Updating...' : 'Refresh'}
            </button>

          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">

          {/* ── ALERTS ── */}
          {activeView === 'alerts' && (
            <div className="max-w-[1200px] mx-auto p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold text-white">Alert Center</h2>
                  <div className="flex gap-2">
                    {tier1Count > 0 && <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded">{tier1Count} CRITICAL</span>}
                    {tier2Count > 0 && <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded">{tier2Count} WARNING</span>}
                    {tier3Count > 0 && <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-bold rounded">{tier3Count} INFO</span>}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {lastRefreshed ? `Last checked: ${lastRefreshed.toLocaleTimeString()}` : 'Not yet refreshed'}
                </div>
              </div>

              {visibleAlerts.length === 0 ? (
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">All Clear</h3>
                  <p className="text-gray-400">No alerts for your current positions.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {[1, 2, 3].map(tier => {
                    const tierAlerts = visibleAlerts.filter(a => a.tier === tier);
                    if (tierAlerts.length === 0) return null;
                    const tierConfig = {
                      1: { icon: <AlertCircle className="w-4 h-4 text-red-400" />, label: 'Tier 1 — Critical', color: 'red', Icon: AlertCircle, bg: 'bg-red-500/5', border: 'border-red-500/30', text: 'text-red-400' },
                      2: { icon: <AlertTriangle className="w-4 h-4 text-yellow-400" />, label: 'Tier 2 — Warning', color: 'yellow', Icon: AlertTriangle, bg: 'bg-yellow-500/5', border: 'border-yellow-500/30', text: 'text-yellow-400' },
                      3: { icon: <Info className="w-4 h-4 text-emerald-400" />, label: 'Tier 3 — Info', color: 'emerald', Icon: Info, bg: 'bg-emerald-500/5', border: 'border-emerald-500/30', text: 'text-emerald-400' },
                    }[tier];
                    return (
                      <div key={tier}>
                        <div className="flex items-center gap-2 mb-3">{tierConfig.icon}<span className={`${tierConfig.text} font-bold text-sm uppercase tracking-wider`}>{tierConfig.label}</span></div>
                        {tierAlerts.map((alert, idx) => (
                          <div key={alert.id || idx} className={`${tierConfig.bg} border ${tierConfig.border} rounded-lg p-4 mb-2 relative`}>
                            {/* Pro lock for congressional/insider signals on free tier */}
                            {alert.isServer && (alert.category === 'congressional' || alert.category === 'insider') && subscription.tier === 'free' && (
                              <div className="absolute inset-0 bg-[#0a0a0a]/80 rounded-lg flex items-center justify-between px-4 z-10">
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-400">🔒</span>
                                  <span className="text-sm text-gray-300">
                                    <span className="font-bold text-white">{alert.ticker}</span> — {alert.category === 'congressional' ? 'Government' : 'Insider'} alert
                                  </span>
                                </div>
                                <button
                                  onClick={handleUpgrade}
                                  disabled={stripeLoading}
                                  className="px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded text-xs font-semibold transition-colors whitespace-nowrap"
                                >
                                  Upgrade to Pro
                                </button>
                              </div>
                            )}
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono font-bold text-white">{alert.ticker}</span>
                                  <span className={`text-xs ${tierConfig.text} font-semibold`}>{tier === 1 ? 'CRITICAL' : tier === 2 ? 'WARNING' : 'INFO'}</span>
                                  {alert.isServer && <span className="text-xs bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">SIGNAL</span>}
                                  {alert.timestamp && <span className="text-xs text-gray-600 ml-2">{alert.timestamp.toLocaleTimeString()}</span>}
                                </div>
                                <p className="text-sm text-gray-300 mb-1">{alert.message}</p>
                                <p className="text-xs text-gray-500">→ {alert.action}</p>
                                {alert.raw_value && alert.threshold && (
                                  <p className="text-xs text-gray-600 mt-1">Value: {typeof alert.raw_value === 'number' ? alert.raw_value.toFixed(1) : alert.raw_value}% | Threshold: {alert.threshold}%</p>
                                )}
                              </div>
                              <button 
                                onClick={() => alert.isServer ? acknowledgeAlert(alert.id) : dismissAlert(alerts.indexOf(alert))} 
                                className="ml-4 p-1 text-gray-600 hover:text-gray-300 transition-colors flex-shrink-0" 
                                title={alert.isServer ? "Acknowledge" : "Dismiss"}
                              >
                                {alert.isServer ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  {dismissedAlerts.size > 0 && (
                    <button onClick={() => setDismissedAlerts(new Set())} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                      Restore {dismissedAlerts.size} dismissed alert{dismissedAlerts.size > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── ANALYTICS ── */}
          {activeView === 'analytics' && (
            <div className="max-w-[1200px] mx-auto p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Analytics</h2>

              {/* Top stat row */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Positions</div>
                  <div className="text-3xl font-bold text-white">{positions.length}</div>
                  <div className="text-xs text-gray-600 mt-1">across {[...new Set(positions.map(p => p.sector))].length} categories</div>
                </div>
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Annual Income</div>
                  <div className="text-3xl font-bold text-emerald-400">${(portfolio.monthlyIncome * 12).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                  <div className="text-xs text-gray-600 mt-1">${portfolio.monthlyIncome.toFixed(0)}/mo average</div>
                </div>
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Portfolio Yield</div>
                  <div className="text-3xl font-bold text-blue-400">{portfolioYield.toFixed(2)}%</div>
                  <div className="text-xs text-gray-600 mt-1">on current market value</div>
                </div>
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Total Return</div>
                  <div className={`text-3xl font-bold ${totalGainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totalGainLossPct >= 0 ? '+' : ''}{totalGainLossPct.toFixed(1)}%
                  </div>
                  <div className={`text-xs mt-1 ${totalGainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totalGainLoss >= 0 ? '+' : ''}${Math.abs(totalGainLoss).toLocaleString('en-US', { maximumFractionDigits: 0 })} unrealized
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Allocation breakdown */}
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Allocation by Position</h3>
                  <div className="space-y-3">
                    {[...positions].sort((a,b) => (b.shares*b.price) - (a.shares*a.price)).map(p => {
                      const val = p.shares * p.price;
                      const pct = portfolio.totalValue > 0 ? (val / portfolio.totalValue * 100) : 0;
                      const color = POSITION_TYPE_COLORS[p.sector] || '#6b7280';
                      return (
                        <div key={p.ticker}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                              <span className="font-mono font-bold text-white text-sm">{p.ticker}</span>
                              <span className="text-xs text-gray-500">{p.sector}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-500">{pct.toFixed(1)}%</span>
                              <span className="text-sm font-semibold text-white w-20 text-right">${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                            </div>
                          </div>
                          <div className="w-full bg-[#1a1a1a] rounded-full h-1.5">
                            <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Income by position */}
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Monthly Income by Position</h3>
                  {positions.filter(p => p.monthlyIncome > 0).length === 0 ? (
                    <div className="text-center py-8 text-gray-600 text-sm">No dividend income tracked yet</div>
                  ) : (
                    <div className="space-y-3">
                      {[...positions].filter(p => p.monthlyIncome > 0).sort((a,b) => b.monthlyIncome - a.monthlyIncome).map(p => {
                        const totalMonthly = positions.reduce((s, x) => s + x.monthlyIncome, 0);
                        const pct = totalMonthly > 0 ? (p.monthlyIncome / totalMonthly * 100) : 0;
                        return (
                          <div key={p.ticker}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-mono font-bold text-white text-sm">{p.ticker}</span>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-500">{pct.toFixed(1)}%</span>
                                <span className="text-sm font-semibold text-emerald-400 w-20 text-right">${p.monthlyIncome.toFixed(0)}/mo</span>
                              </div>
                            </div>
                            <div className="w-full bg-[#1a1a1a] rounded-full h-1.5">
                              <div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                      <div className="pt-2 mt-2 border-t border-[#1a1a1a] flex justify-between">
                        <span className="text-xs text-gray-500">Total monthly</span>
                        <span className="text-sm font-bold text-emerald-400">${portfolio.monthlyIncome.toFixed(0)}/mo</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Category breakdown */}
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">By Category</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(
                    positions.reduce((acc, p) => {
                      const cat = p.sector || 'Other';
                      if (!acc[cat]) acc[cat] = { value: 0, income: 0, count: 0 };
                      acc[cat].value += p.shares * p.price;
                      acc[cat].income += p.monthlyIncome;
                      acc[cat].count += 1;
                      return acc;
                    }, {})
                  ).sort((a,b) => b[1].value - a[1].value).map(([cat, stats]) => {
                    const pct = portfolio.totalValue > 0 ? (stats.value / portfolio.totalValue * 100) : 0;
                    const color = POSITION_TYPE_COLORS[cat] || '#6b7280';
                    return (
                      <div key={cat} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                          <span className="text-sm font-semibold text-white">{cat}</span>
                        </div>
                        <div className="text-xl font-bold text-white mb-0.5">${stats.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                        <div className="text-xs text-gray-500 mb-2">{pct.toFixed(1)}% of portfolio · {stats.count} position{stats.count > 1 ? 's' : ''}</div>
                        {stats.income > 0 && <div className="text-xs text-emerald-400">${stats.income.toFixed(0)}/mo income</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}


          {/* ── METALS ── */}
          {activeView === 'metals' && <MetalsTracker />}

          {/* ── EDGE TERMINAL ── */}
          {activeView === 'edge' && <EdgeTerminal />}

          {/* ── INTELLIGENCE ── */}
          {activeView === 'intelligence' && (
            <Intelligence
              session={session}
              subscription={subscription}
              positions={positions}
              onUpgrade={handleUpgrade}
              stripeLoading={stripeLoading}
            />
          )}

          {/* ── NEWS FEED ── */}
          {activeView === 'news' && (
            <NewsFeed />
          )}

          {/* ── ETF X-RAY ── */}
          {activeView === 'etfxray' && (
            <ETFXRay
              session={session}
              userTier={subscription.tier}
              portfolioTickers={positions.map(p => p.ticker)}
              onUpgrade={handleUpgrade}
            />
          )}

          {/* ── PEER PULSE ── */}
          {activeView === 'peerpulse' && (
            <PeerPulse
              session={session}
              userTier={subscription.tier}
              portfolioTickers={positions.map(p => p.ticker)}
              onUpgrade={handleUpgrade}
            />
          )}

          {/* ── COMPARABLE SCREENER ── */}
          {activeView === 'screener' && (
            <ComparableScreener
              session={session}
              userTier={subscription.tier}
              portfolioTickers={positions.map(p => p.ticker)}
              onUpgrade={handleUpgrade}
            />
          )}

          {/* ── SETTINGS ── */}
          {activeView === 'settings' && (
            <div className="max-w-[800px] mx-auto p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Settings</h2>

              {/* Account */}
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Account</h3>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-white">{user.email}</div>
                      {subscription.tier === 'founder' && <span className="text-xs bg-amber-500/15 text-amber-400 border border-amber-500/25 px-2 py-0.5 rounded font-bold">✦ FOUNDER EDITION</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Member since {new Date(user.created_at || Date.now()).toLocaleDateString()}</div>
                  </div>
                  <button onClick={handleSignOut} className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-400 rounded text-sm transition-colors">Sign Out</button>
                </div>
                <div className="border-t border-[#1a1a1a] pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Current Plan</div>
                      <div className="flex items-center gap-2">
                        {subscription.tier === 'founder' ? (
                          <>
                            <span className="text-sm font-bold text-amber-400">Founder Edition</span>
                            <span className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded">∞ positions</span>
                          </>
                        ) : subscription.tier === 'pro' || subscription.tier === 'premium' ? (
                          <>
                            <span className="text-sm font-bold text-cyan-400">Pro</span>
                            <span className="text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-2 py-0.5 rounded">20 positions</span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-bold text-white">Free</span>
                            <span className="text-xs bg-[#1a1a1a] text-gray-400 px-2 py-0.5 rounded">5 positions max</span>
                          </>
                        )}
                      </div>
                    </div>
                    {subscription.tier === 'founder' ? (
                      <span className="text-xs text-amber-400/60">Full Access</span>
                    ) : subscription.tier === 'pro' || subscription.tier === 'premium' ? (
                      <button
                        onClick={handleManageSubscription}
                        disabled={stripeLoading}
                        className="px-3 py-1.5 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-gray-400 hover:text-white border border-[#2a2a2a] rounded text-xs transition-colors disabled:opacity-50"
                      >
                        {stripeLoading ? '...' : 'Manage'}
                      </button>
                    ) : (
                      <button
                        onClick={handleUpgrade}
                        disabled={stripeLoading}
                        className="px-3 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/20 rounded text-xs font-semibold transition-colors disabled:opacity-50"
                      >
                        {stripeLoading ? '...' : 'Upgrade to Pro'}
                      </button>
                    )}
                  </div>
                  {/* Payment failed warning */}
                  {!subscription.active && (subscription.tier === 'pro' || subscription.tier === 'premium' || subscription.tier === 'founder') && (
                    <div className="mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded text-xs text-amber-400 flex items-center justify-between gap-2">
                      <span>⚠ Payment issue — update your card to keep Pro access</span>
                      <button onClick={handleManageSubscription} className="underline whitespace-nowrap hover:text-amber-300">Fix now</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Portfolio stats */}
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Portfolio Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    ['Positions', positions.length],
                    ['Portfolio Value', `$${portfolio.totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`],
                    ['Monthly Income', `$${portfolio.monthlyIncome.toFixed(0)}`],
                    ['Annual Income', `$${(portfolio.monthlyIncome * 12).toFixed(0)}`],
                    ['Portfolio Yield', `${portfolioYield.toFixed(2)}%`],
                    ['Total Return', `${totalGainLossPct >= 0 ? '+' : ''}${totalGainLossPct.toFixed(2)}%`],
                  ].map(([label, val]) => (
                    <div key={label} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">{label}</div>
                      <div className="text-sm font-bold text-white">{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Coming soon */}
              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Coming Soon</h3>
                <div className="space-y-2">
                  {['Email alerts when dividends are cut', 'SMS notifications for ex-date countdowns', 'Pro: ETF holdings breakdown panel', 'Pro: Analyst price targets & consensus', 'Pro: COT silver positioning signal'].map(item => (
                    <div key={item} className="flex items-center gap-2 text-xs text-gray-600">
                      <div className="w-1 h-1 rounded-full bg-gray-700" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── DASHBOARD ── */}
          {activeView === 'dashboard' && (
            <div className="max-w-[1800px] mx-auto p-8">
              {positions.length === 0 ? (
                <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-12 text-center">
                  <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Positions Yet</h3>
                  <p className="text-gray-400 mb-6">Add your first position to start tracking</p>
                  <button onClick={() => setShowAddModal(true)} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors inline-flex items-center gap-2"><Plus className="w-5 h-5" />Add Your First Position</button>
                </div>
              ) : (
                <>
                  {/* ── STAT CARDS ── */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    {/* Total Gain/Loss */}
                    <div 
                      className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden cursor-pointer group"
                      onMouseEnter={() => setShimmerGain(true)}
                      onMouseLeave={() => setShimmerGain(false)}
                    >
                      <div className="h-1 bg-[#15803d]" />
                      <div className="p-5 relative overflow-hidden">
                        {shimmerGain && (
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                        )}
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">Total Gain / Loss</span>
                          <TrendingUp className={`w-5 h-5 ${totalGainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
                        </div>
                        <div className={`text-3xl font-bold ${totalGainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {totalGainLoss >= 0 ? '+' : ''}${Math.abs(totalGainLoss).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </div>
                        <div className={`text-xs mt-1 ${totalGainLossPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {totalGainLossPct >= 0 ? '+' : ''}{totalGainLossPct.toFixed(2)}% vs cost basis
                        </div>
                      </div>
                    </div>

                    {/* Cost Basis */}
                    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden">
                      <div className="h-1 bg-[#15803d]" />
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">Cost Basis</span>
                          <DollarSign className="w-5 h-5 text-[#15803d]" />
                        </div>
                        <div className="text-3xl font-bold text-white">
                          ${portfolio.totalCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Current: ${portfolio.totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                        </div>
                      </div>
                    </div>

                    {/* Portfolio Yield */}
                    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden">
                      <div className="h-1 bg-[#15803d]" />
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">Portfolio Yield</span>
                          <Percent className="w-5 h-5 text-[#15803d]" />
                        </div>
                        <div className="text-3xl font-bold text-white">{portfolioYield.toFixed(2)}%</div>
                        <div className="text-xs text-gray-500 mt-1">${annualIncome.toFixed(0)}/yr annual income</div>
                      </div>
                    </div>

                    {/* Next Ex-Date - Click to open calendar */}
                    <div 
                      className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden cursor-pointer hover:border-[#2a2a2a] transition-colors group"
                      onClick={() => { setShowCalendarModal(true); fetchCalendarEvents(); }}
                    >
                      <div className="h-1 bg-[#15803d]" />
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">Next Ex-Date</span>
                          <Calendar className="w-5 h-5 text-amber-400 group-hover:scale-110 transition-transform" />
                        </div>
                        {(() => {
                          const today = new Date();
                          const upcoming = positions
                            .filter(p => p.nextExDate)
                            .map(p => ({ ticker: p.ticker, date: new Date(p.nextExDate), sector: p.sector }))
                            .filter(p => p.date >= today)
                            .sort((a, b) => a.date - b.date);
                          if (upcoming.length === 0) return (
                            <>
                              <div className="text-3xl font-bold text-gray-600">—</div>
                              <div className="text-xs text-gray-600 mt-1">No upcoming ex-dates</div>
                            </>
                          );
                          const next = upcoming[0];
                          const daysTo = Math.ceil((next.date - today) / (1000 * 60 * 60 * 24));
                          return (
                            <>
                              <div className="flex items-baseline gap-2 mb-3">
                                <div className="text-3xl font-bold text-amber-400">{daysTo}d</div>
                                <div className="text-sm text-gray-400">{next.ticker} — {next.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                              </div>
                              <div className="space-y-1.5">
                                {upcoming.slice(0, 4).map((e, i) => {
                                  const d = Math.ceil((e.date - today) / (1000 * 60 * 60 * 24));
                                  const urgent = d <= 7;
                                  const color = POSITION_TYPE_COLORS[e.sector] || '#f59e0b';
                                  return (
                                    <div key={i} className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                        <span className={`text-xs font-mono font-bold ${urgent ? 'text-amber-300' : 'text-gray-400'}`}>{e.ticker}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">{e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${urgent ? 'bg-amber-500/20 text-amber-300' : 'bg-[#1a1a1a] text-gray-500'}`}>{d}d</span>
                                      </div>
                                    </div>
                                  );
                                })}
                                {upcoming.length > 4 && <div className="text-xs text-gray-600 mt-1">+{upcoming.length - 4} more</div>}
                              </div>
                            </>
                          );
                        })()}
                        <div className="text-xs text-[#15803d] mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to view calendar →</div>
                      </div>
                    </div>
                  </div>

                  {/* ── ALLOCATION BAR ── */}
                  <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-5 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500 uppercase tracking-wider">Portfolio Allocation</span>
                      <span className="text-xs text-gray-600">${portfolio.totalValue.toLocaleString('en-US', { maximumFractionDigits: 0 })} total</span>
                    </div>
                    {/* Stacked bar */}
                    <div className="flex h-4 rounded overflow-hidden mb-3 gap-px">
                      {positions.map(p => {
                        const pct = portfolio.totalValue > 0 ? ((p.shares * p.price) / portfolio.totalValue * 100) : 0;
                        const color = POSITION_TYPE_COLORS[p.sector] || '#6b7280';
                        return <div key={p.ticker} style={{ width: `${pct}%`, backgroundColor: color }} title={`${p.ticker}: ${pct.toFixed(1)}%`} />;
                      })}
                    </div>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-1">
                      {positions.map(p => {
                        const val = p.shares * p.price;
                        const pct = portfolio.totalValue > 0 ? (val / portfolio.totalValue * 100) : 0;
                        const color = POSITION_TYPE_COLORS[p.sector] || '#6b7280';
                        return (
                          <div key={p.ticker} className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            <span className="text-xs font-mono font-bold text-white">{p.ticker}</span>
                            <span className="text-xs text-gray-500">{pct.toFixed(1)}%</span>
                            <span className="text-xs text-gray-600">${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── TABLE ── */}
                  <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden mb-8">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#1a1a1a]">
                          {[
                            { label: 'Position', key: 'ticker' },
                            { label: 'Shares', key: 'shares' },
                            { label: 'Market Value', key: 'marketValue' },
                            { label: 'Day Change', key: 'dayChangePct' },
                            { label: 'Gain/Loss', key: 'gainLoss' },
                            { label: 'Cost Basis', key: 'costBasis' },
                            { label: 'Yield', key: 'currentYield' },
                            { label: 'Income', key: 'monthlyIncome' },
                          ].map((col, i) => (
                            <th 
                              key={col.key} 
                              className={`${i === 0 ? 'text-left' : 'text-right'} px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-300 transition-colors select-none`}
                              onClick={() => {
                                setSortConfig(prev => ({
                                  key: col.key,
                                  direction: prev.key === col.key && prev.direction === 'desc' ? 'asc' : 'desc'
                                }));
                              }}
                            >
                              <div className={`flex items-center gap-1 ${i === 0 ? '' : 'justify-end'}`}>
                                {col.label}
                                {sortConfig.key === col.key && (
                                  <span className="text-[#15803d]">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
                                )}
                              </div>
                            </th>
                          ))}
                          <th className="px-2 py-2.5 text-center">
                            <button onClick={() => setShowAddModal(true)} className="flex items-center gap-1 mx-auto text-xs font-semibold text-[#15803d] hover:text-emerald-400 uppercase tracking-wider transition-colors">
                              <Plus className="w-3.5 h-3.5" /> Add
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...positions]
                          .map(p => ({
                            ...p,
                            marketValue: p.shares * p.price,
                            gainLoss: (p.shares * p.price) - (p.shares * p.costBasis),
                          }))
                          .sort((a, b) => {
                            if (!sortConfig.key) return 0;
                            let aVal = a[sortConfig.key];
                            let bVal = b[sortConfig.key];
                            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                            return 0;
                          })
                          .map((position) => {
                          const marketValue = position.shares * position.price;
                          const totalCost = position.shares * position.costBasis;
                          const gainLoss = marketValue - totalCost;
                          const gainLossPct = ((gainLoss / totalCost) * 100).toFixed(2);
                          const isExpanded = expandedRows.has(position.ticker);
                          const posAlerts = visibleAlerts.filter(a => a.ticker === position.ticker);
                          const hasAlert = posAlerts.some(a => a.tier <= 2);
                          const dotColor = POSITION_TYPE_COLORS[position.sector] || '#6b7280';

                          return (
                            <React.Fragment key={position.id}>
                              <tr onClick={() => toggleRow(position.ticker)} className={`border-b border-[#1a1a1a] hover:bg-[#141414] transition-colors cursor-pointer ${isExpanded ? 'bg-white/5 border-t-2 border-t-white/40' : ''}`}>>
                                {/* Position */}
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-2">
                                    {hasAlert
                                      ? <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" title="Has alerts" />
                                      : <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} title={position.sector} />
                                    }
                                    <div>
                                      <div className="font-mono font-bold text-white">{position.ticker}</div>
                                      <div className="text-xs text-gray-500">{position.sector}</div>
                                    </div>
                                  </div>
                                </td>
                                {/* Shares */}
                                <td className="px-4 py-2 text-right text-sm font-semibold text-white">{position.shares}</td>
                                {/* Market Value */}
                                <td className="px-4 py-2 text-right text-sm font-semibold text-white">${marketValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                                {/* Day Change */}
                                <td className="px-4 py-2 text-right">
                                  <div className={`text-sm font-semibold ${position.dayChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{position.dayChange >= 0 ? '+' : ''}${Math.abs(position.dayChange).toFixed(2)}</div>
                                  <div className={`text-xs ${position.dayChangePct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{position.dayChangePct >= 0 ? '+' : ''}{position.dayChangePct.toFixed(2)}%</div>
                                </td>
                                {/* Gain/Loss */}
                                <td className="px-4 py-2 text-right">
                                  <div className={`text-sm font-semibold ${gainLoss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{gainLoss >= 0 ? '+' : ''}${Math.abs(gainLoss).toFixed(0)}</div>
                                  <div className={`text-xs ${gainLossPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{gainLossPct >= 0 ? '+' : ''}{gainLossPct}%</div>
                                </td>
                                {/* Cost Basis */}
                                <td className="px-4 py-2 text-right text-sm text-gray-400">${position.costBasis.toFixed(2)}</td>
                                {/* Yield */}
                                <td className="px-4 py-2 text-right">
                                  {position.currentYield > 0
                                    ? <div className="text-sm font-semibold text-white">{position.currentYield.toFixed(2)}%</div>
                                    : <span className="text-xs text-gray-600">—</span>
                                  }
                                </td>
                                {/* Income */}
                                <td className="px-4 py-2 text-right">
                                  {position.monthlyIncome > 0
                                    ? <div className="text-sm font-semibold text-emerald-400">${position.monthlyIncome.toFixed(0)}/mo</div>
                                    : <span className="text-xs text-gray-600">No dividend</span>
                                  }
                                </td>
                                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    <button onClick={() => startEdit(position)} className="p-1.5 hover:bg-[#2a2a2a] rounded transition-colors"><Edit className="w-4 h-4 text-blue-400" /></button>
                                    <button onClick={() => { setSelectedPositionForOption(position); setShowOptionsModal(true); }} className="p-1.5 hover:bg-[#2a2a2a] rounded transition-colors"><PieChart className="w-4 h-4 text-purple-400" /></button>
                                    <button onClick={() => handleDeletePosition(position.id)} className="p-1.5 hover:bg-[#2a2a2a] rounded transition-colors"><Trash2 className="w-4 h-4 text-red-400" /></button>
                                    <button onClick={() => toggleRow(position.ticker)} className="p-1.5 hover:bg-[#2a2a2a] rounded transition-colors">{isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}</button>
                                  </div>
                                </td>
                              </tr>

                              {/* Expanded row */}
                              {isExpanded && (
                                <tr className="border-b border-[#1a1a1a]">
                                  <td colSpan="9" className="p-0">
                                    <div className="bg-[#0a0a0a] p-6">
                                      <div className="grid grid-cols-3 gap-6">
                                        <div>
                                          <h4 className="text-sm font-semibold text-gray-400 mb-3">Position Details</h4>
                                          <div className="grid grid-cols-2 gap-3">
                                            {[['Shares', position.shares], ['Cost Basis', `$${position.costBasis.toFixed(2)}`], ['Yield', `${position.currentYield.toFixed(2)}%`], ['YOC', `${position.yieldOnCost.toFixed(2)}%`]].map(([label, val]) => (
                                              <div key={label} className="bg-[#0f0f0f] border border-[#1a1a1a] rounded p-3"><div className="text-xs text-gray-500">{label}</div><div className="text-lg font-semibold text-white">{val}</div></div>
                                            ))}
                                            <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded p-3 col-span-2"><div className="text-xs text-gray-500">Next Ex-Date</div><div className="text-sm font-semibold text-white">{position.nextExDate || 'N/A'}</div></div>
                                          </div>
                                        </div>
                                        <div>
                                          <h4 className="text-sm font-semibold text-gray-400 mb-3">Health & Alerts</h4>
                                          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded p-4 mb-3">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-xs text-gray-500">Overall Health</span>
                                              <span className={`text-lg font-bold ${position.health >= 80 ? 'text-emerald-400' : position.health >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>{position.health}</span>
                                            </div>
                                            <div className="w-full bg-[#1a1a1a] rounded-full h-2"><div className={`h-2 rounded-full ${position.health >= 80 ? 'bg-emerald-400' : position.health >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`} style={{ width: `${position.health}%` }} /></div>
                                          </div>
                                          {posAlerts.length > 0 && (
                                            <div className="space-y-2">
                                              {posAlerts.slice(0, 3).map((a, i) => (
                                                <div key={i} className={`rounded p-2 text-xs border ${a.tier === 1 ? 'bg-red-500/10 border-red-500/30 text-red-300' : a.tier === 2 ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'}`}>{a.message}</div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <div>
                                          <h4 className="text-sm font-semibold text-gray-400 mb-3">Price Chart</h4>
                                          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded overflow-hidden">
                                            <div style={{ height: '200px', width: '100%' }}><div id={`tradingview_${position.ticker}`} style={{ height: '100%', width: '100%' }}></div></div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}

                        {/* ── TOTALS ROW ── */}
                        {(() => {
                          const totMarketVal = positions.reduce((s, p) => s + p.shares * p.price, 0);
                          const totCost = positions.reduce((s, p) => s + p.shares * p.costBasis, 0);
                          const totGL = totMarketVal - totCost;
                          const totGLPct = totCost > 0 ? (totGL / totCost) * 100 : 0;
                          const totIncome = positions.reduce((s, p) => s + p.monthlyIncome, 0);
                          const avgYield = totMarketVal > 0 ? ((totIncome * 12) / totMarketVal) * 100 : 0;
                          return (
                            <tr className="border-t-2 border-[#2a2a2a] bg-[#0f0f0f]">
                              {/* Position */}
                              <td className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Total</td>
                              {/* Shares */}
                              <td className="px-4 py-2 text-right text-xs text-gray-500">{positions.length} positions</td>
                              {/* Market Value */}
                              <td className="px-4 py-2 text-right text-sm font-bold text-white">${totMarketVal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                              {/* Day Change */}
                              <td className="px-4 py-2" />
                              {/* Gain/Loss */}
                              <td className="px-4 py-2 text-right">
                                <div className={`text-sm font-bold ${totGL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{totGL >= 0 ? '+' : ''}${Math.abs(totGL).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                                <div className={`text-xs ${totGLPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{totGLPct >= 0 ? '+' : ''}{totGLPct.toFixed(2)}%</div>
                              </td>
                              {/* Cost Basis */}
                              <td className="px-4 py-2 text-right text-sm text-gray-400">${totCost.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                              {/* Yield */}
                              <td className="px-4 py-2 text-right text-sm text-white">{avgYield.toFixed(2)}%</td>
                              {/* Income */}
                              <td className="px-4 py-2 text-right text-sm font-bold text-emerald-400">${totIncome.toFixed(0)}/mo</td>
                              {/* Actions */}
                              <td className="px-2 py-2" />
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-bold text-white">Add Position</h3><button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button></div>
            <form onSubmit={handleAddPosition} className="space-y-4">
              <div><label className="block text-sm text-gray-400 mb-2">Ticker</label><input type="text" value={formData.ticker} onChange={(e) => setFormData({ ...formData, ticker: e.target.value.toUpperCase() })} required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" placeholder="JEPQ" maxLength={10} /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Shares</label><input type="number" step="0.0001" value={formData.shares} onChange={(e) => setFormData({ ...formData, shares: e.target.value })} required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" placeholder="100" /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Cost Basis (per share)</label><input type="number" step="0.01" value={formData.cost_basis} onChange={(e) => setFormData({ ...formData, cost_basis: e.target.value })} required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" placeholder="52.50" /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Position Type</label>
                <select value={formData.position_type} onChange={(e) => setFormData({ ...formData, position_type: e.target.value })} className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500">
                  <option>Income Engine</option><option>Wealth Core</option><option>Volatility Engine</option><option>Hard Asset</option><option>Other</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowAddModal(false); setFormData({ ticker: '', shares: '', cost_basis: '', position_type: 'Income Engine', notes: '' }); }} className="flex-1 px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded hover:bg-[#2a2a2a] transition-colors">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">Add Position</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-bold text-white">Edit Position</h3><button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button></div>
            <form onSubmit={handleEditPosition} className="space-y-4">
              <div><label className="block text-sm text-gray-400 mb-2">Ticker</label><input type="text" value={formData.ticker} disabled className="w-full bg-[#0a0a0a] border border-[#1a1a1a] rounded px-4 py-2 text-gray-600 cursor-not-allowed" /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Shares</label><input type="number" step="0.0001" value={formData.shares} onChange={(e) => setFormData({ ...formData, shares: e.target.value })} required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Cost Basis (per share)</label><input type="number" step="0.01" value={formData.cost_basis} onChange={(e) => setFormData({ ...formData, cost_basis: e.target.value })} required className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500" /></div>
              <div><label className="block text-sm text-gray-400 mb-2">Position Type</label>
                <select value={formData.position_type} onChange={(e) => setFormData({ ...formData, position_type: e.target.value })} className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-4 py-2 text-white focus:outline-none focus:border-gray-500">
                  <option>Income Engine</option><option>Wealth Core</option><option>Volatility Engine</option><option>Hard Asset</option><option>Other</option>
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingPosition(null); setFormData({ ticker: '', shares: '', cost_basis: '', position_type: 'Income Engine', notes: '' }); }} className="flex-1 px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded hover:bg-[#2a2a2a] transition-colors">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors">Update Position</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Options Modal */}
      {showOptionsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-bold text-white">Add Option — {selectedPositionForOption?.ticker}</h3><button onClick={() => setShowOptionsModal(false)} className="text-gray-400 hover:text-white"><X className="w-6 h-6" /></button></div>
            <div className="space-y-4">
              <p className="text-gray-400 text-sm">Option tracking coming soon.</p>
              <p className="text-xs text-gray-500">Planned: Covered Calls, Cash-Secured Puts, premium tracking, expiration alerts.</p>
              <button onClick={() => setShowOptionsModal(false)} className="w-full px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      {showCalendarModal && (() => {
        // Calendar helper functions
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const today = new Date();

        // Parse YYYY-MM-DD as LOCAL time (not UTC) to avoid off-by-one timezone shift
        const parseLocalDate = (str) => {
          if (!str) return null;
          if (typeof str === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
            const [y, m, d] = str.split('-').map(Number);
            return new Date(y, m - 1, d);
          }
          return new Date(str);
        };

        // Adjust weekend dates: Sat→Fri (ex-div), Sun→Mon (pay dates / earnings)
        const adjustWeekend = (dateStr, type) => {
          const d = parseLocalDate(dateStr);
          if (!d) return d;
          const dow = d.getDay();
          if (dow === 6) { // Saturday → Friday
            d.setDate(d.getDate() - 1);
          } else if (dow === 0) { // Sunday → Monday
            d.setDate(d.getDate() + 1);
          }
          return d;
        };

        // Mon-based week offset for first day (Mon=0 … Fri=4)
        const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0=Sun
        // Convert to Mon-based: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, skip Sat/Sun
        const monBasedFirst = (firstDayOfMonth + 6) % 7; // Mon=0, Sat=6, Sun=6→clamp
        const firstWeekdayOffset = Math.min(monBasedFirst, 4); // clamp to 0-4

        const prevMonth = () => { setCalendarMonth(new Date(year, month - 1, 1)); setSelectedCalDay(null); };
        const nextMonth = () => { setCalendarMonth(new Date(year, month + 1, 1)); setSelectedCalDay(null); };

        // Get adjusted events for a given day (weekday only)
        const getEventsForDay = (day) => {
          return calendarEvents.filter(e => {
            const d = adjustWeekend(e.date, e.type);
            if (!d) return false;
            return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
          });
        };

        // Generate .ics file content
        const generateICS = (event) => {
          const d = parseLocalDate(event.date);
          const pad = n => String(n).padStart(2,'0');
          const dateStr = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
          const nextDay = new Date(d); nextDay.setDate(nextDay.getDate()+1);
          const endStr = `${nextDay.getFullYear()}${pad(nextDay.getMonth()+1)}${pad(nextDay.getDate())}`;
          const description = event.type === 'earnings' ? 'Earnings report' : event.type === 'dividend' ? 'Ex-dividend date' : 'Dividend payment date';
          return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//TickerTap//Income Calendar//EN
BEGIN:VEVENT
UID:${event.ticker}-${event.type}-${dateStr}@tickertap.io
DTSTAMP:${dateStr}T000000Z
DTSTART;VALUE=DATE:${dateStr}
DTEND;VALUE=DATE:${endStr}
SUMMARY:${event.ticker} ${event.label}
DESCRIPTION:${description} for ${event.ticker}
END:VEVENT
END:VCALENDAR`;
        };

        const downloadICS = (event) => {
          const ics = generateICS(event);
          const blob = new Blob([ics], { type: 'text/calendar' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${event.ticker}-${event.label.replace(/\s/g, '-')}.ics`;
          a.click();
          URL.revokeObjectURL(url);
        };

        const openGoogleCalendar = (event) => {
          const d = parseLocalDate(event.date);
          const pad = n => String(n).padStart(2,'0');
          const dateStr = `${d.getFullYear()}${pad(d.getMonth()+1)}${pad(d.getDate())}`;
          const title = encodeURIComponent(`${event.ticker} ${event.label}`);
          const description = event.type === 'earnings' ? 'Earnings report' : event.type === 'dividend' ? 'Ex-dividend date' : 'Dividend payment date';
          const details = encodeURIComponent(`${description} for ${event.ticker}`);
          window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dateStr}/${dateStr}&details=${details}`, '_blank');
        };

        // Event color helper
        const getEventStyles = (event) => {
          const isUserPos = event.isUserPosition;
          if (event.type === 'earnings') {
            return isUserPos
              ? 'bg-purple-500/25 text-purple-100 ring-1 ring-purple-400'
              : 'bg-purple-400/10 text-purple-300';
          } else if (event.type === 'payment') {
            return isUserPos
              ? 'bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-400'
              : 'bg-emerald-400/10 text-emerald-300';
          } else {
            // dividend / ex-div
            return isUserPos
              ? 'bg-amber-500/25 text-amber-100 ring-1 ring-amber-400'
              : 'bg-amber-400/10 text-amber-300';
          }
        };

        const getEventDot = (type) => {
          if (type === 'earnings') return 'bg-purple-400';
          if (type === 'payment') return 'bg-emerald-400';
          return 'bg-amber-400';
        };

        const getEventTypeLabel = (type) => {
          if (type === 'earnings') return { label: 'Earnings', color: 'text-purple-400' };
          if (type === 'payment') return { label: 'Pay Date', color: 'text-emerald-400' };
          return { label: 'Ex-Div', color: 'text-amber-400' };
        };

        return (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-3 z-50">
            <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg w-full max-w-[85vw] h-[92vh] overflow-hidden flex flex-col relative">
              {/* Header */}
              <div className="px-5 py-3 border-b border-[#1a1a1a] flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-shrink-0">
                  <h3 className="text-xl font-bold text-white">Income Calendar</h3>
                  <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" /><span className="text-gray-400">Ex-Div</span></div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" /><span className="text-gray-400">Payment</span></div>
                    <div className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-400 inline-block" /><span className="text-gray-400">Earnings</span></div>
                    <div className="flex items-center gap-1 pl-2 border-l border-[#2a2a2a]"><span className="w-2.5 h-2.5 rounded-full ring-1 ring-white bg-white/20 inline-block" /><span className="text-gray-400">Yours</span></div>
                  </div>
                </div>
                {/* Month nav */}
                <div className="flex items-center gap-3">
                  <button onClick={prevMonth} className="p-1.5 hover:bg-[#1a1a1a] rounded transition-colors text-gray-400 hover:text-white">‹</button>
                  <h4 className="text-base font-semibold text-white w-36 text-center">{calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h4>
                  <button onClick={nextMonth} className="p-1.5 hover:bg-[#1a1a1a] rounded transition-colors text-gray-400 hover:text-white">›</button>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => fetchCalendarEvents()} className="text-xs text-gray-500 hover:text-white px-2 py-1 hover:bg-[#1a1a1a] rounded transition-colors">↻ Refresh</button>
                  <button onClick={() => setShowCalendarModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
              </div>

              {/* Search + Sort toolbar */}
              <div className="px-5 py-2.5 border-b border-[#1a1a1a] flex items-center gap-3 bg-[#0a0a0a]">
                <div className="relative flex-1 max-w-sm">
                  <input
                    type="text"
                    value={calendarSearch}
                    onChange={e => {
                      const val = e.target.value.toUpperCase();
                      setCalendarSearch(val);
                      setSelectedCalDay(null); // never auto-open popup on search
                      if (val.length >= 2) {
                        // Just navigate to the month containing the first match
                        const now = new Date();
                        const match = calendarEvents
                          .filter(ev => ev.ticker.includes(val))
                          .map(ev => ({ ev, d: adjustWeekend(ev.date, ev.type) }))
                          .filter(({ d }) => d && d >= now)
                          .sort((a, b) => a.d - b.d)[0];
                        if (match) {
                          const { d } = match;
                          setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                        }
                      }
                    }}
                    placeholder="Search ticker — jumps to month…"
                    className="w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#3a3a3a]"
                  />
                  {calendarSearch && (
                    <button onClick={() => { setCalendarSearch(''); setSelectedCalDay(null); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs">✕</button>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500 mr-1">Sort:</span>
                  {[['yours','Yours First'],['alpha','A–Z'],['type','By Type']].map(([val, label]) => (
                    <button key={val} onClick={() => setCalendarSort(val)}
                      className={`text-xs px-2.5 py-1 rounded transition-colors ${calendarSort === val ? 'bg-[#2a2a2a] text-white' : 'text-gray-500 hover:text-gray-300 hover:bg-[#1a1a1a]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="ml-auto text-xs text-gray-600">
                  {calendarSearch
                    ? calendarEvents.filter(e => e.ticker.includes(calendarSearch)).length > 0
                      ? `${calendarEvents.filter(e => e.ticker.includes(calendarSearch)).length} events found`
                      : `No events found for ${calendarSearch} in the next 60 days`
                    : `${calendarEvents.length} events loaded`}
                </div>
              </div>

              {/* Upcoming events strip — your positions first */}
              <div className="px-5 py-2 border-b border-[#1a1a1a] bg-[#0a0a0a] flex items-center gap-2 overflow-x-auto flex-shrink-0">
                <span className="text-xs text-gray-600 uppercase tracking-wider whitespace-nowrap flex-shrink-0">Upcoming:</span>
                {calendarEvents
                  .filter(e => { const d = adjustWeekend(e.date, e.type); return d && d >= today; })
                  .sort((a, b) => {
                    if (a.isUserPosition && !b.isUserPosition) return -1;
                    if (!a.isUserPosition && b.isUserPosition) return 1;
                    return adjustWeekend(a.date, a.type) - adjustWeekend(b.date, b.type);
                  })
                  .slice(0, 12)
                  .map((event, idx) => {
                    const adjDate = adjustWeekend(event.date, event.type);
                    return (
                      <div key={idx} className={`text-xs px-2 py-1 rounded whitespace-nowrap flex-shrink-0 cursor-pointer ${
                        event.type === 'earnings'
                          ? event.isUserPosition ? 'bg-purple-500/30 text-purple-200 ring-1 ring-purple-400' : 'bg-purple-400/15 text-purple-300'
                          : event.type === 'payment'
                            ? event.isUserPosition ? 'bg-emerald-500/30 text-emerald-200 ring-1 ring-emerald-400' : 'bg-emerald-400/15 text-emerald-300'
                            : event.isUserPosition ? 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-400' : 'bg-amber-400/15 text-amber-300'
                      }`}
                        onClick={() => {
                          const d = adjDate;
                          if (d.getFullYear() === year && d.getMonth() === month) {
                            setSelectedCalDay(d.getDate());
                            setCalendarPopupSearch('');
                          } else {
                            setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
                            setSelectedCalDay(d.getDate());
                            setCalendarPopupSearch('');
                          }
                        }}
                      >
                        <span className="font-mono font-bold">{event.ticker}</span>
                        <span className="ml-1 opacity-60">{adjDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    );
                  })}
                {calendarEvents.length === 0 && (
                  <span className="text-xs text-gray-600 flex items-center gap-1"><span className="animate-pulse">●</span> Loading…</span>
                )}
              </div>

              {/* Calendar grid — Mon–Fri only */}
              <div className="flex-1 p-2 flex flex-col min-h-0">
                <div className="grid grid-cols-5 gap-1 mb-1 flex-shrink-0">
                  {['Mon','Tue','Wed','Thu','Fri'].map(d => (
                    <div key={d} className="text-center text-xs font-semibold text-gray-500 py-1">{d}</div>
                  ))}
                </div>
                <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#1a1a1a transparent' }}>
                  <div className="grid grid-cols-5 gap-1 h-full" style={{ gridTemplateRows: `repeat(${Math.ceil((firstWeekdayOffset + Array.from({length: daysInMonth}).filter((_,i) => { const dow = new Date(year,month,i+1).getDay(); return dow!==0&&dow!==6; }).length) / 5)}, minmax(110px, 1fr))` }}>
                  {/* Empty offset cells for Mon-based week */}
                  {Array.from({ length: firstWeekdayOffset }).map((_, i) => (
                    <div key={`empty-${i}`} className="bg-[#0a0a0a] rounded" />
                  ))}
                  {/* Weekday cells only — skip Sat(6) and Sun(0) */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dow = new Date(year, month, day).getDay();
                    if (dow === 0 || dow === 6) return null; // skip weekends
                    const rawEvents = getEventsForDay(day).filter(e => !calendarSearch || e.ticker.includes(calendarSearch));
                    const dayEvents = [...rawEvents].sort((a, b) => {
                      if (calendarSort === 'yours') {
                        if (a.isUserPosition && !b.isUserPosition) return -1;
                        if (!a.isUserPosition && b.isUserPosition) return 1;
                        return a.ticker.localeCompare(b.ticker);
                      } else if (calendarSort === 'alpha') {
                        return a.ticker.localeCompare(b.ticker);
                      } else {
                        const typeOrder = { dividend: 0, payment: 1, earnings: 2 };
                        return (typeOrder[a.type] ?? 3) - (typeOrder[b.type] ?? 3);
                      }
                    });
                    const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                    const hasUserEvent = dayEvents.some(e => e.isUserPosition);
                    const isSelected = selectedCalDay === day;
                    const MAX_VISIBLE = 5;
                    const overflow = dayEvents.length - MAX_VISIBLE;

                    return (
                      <div key={day}
                        onClick={() => { setSelectedCalDay(isSelected ? null : (dayEvents.length > 0 ? day : null)); setCalendarPopupSearch(''); }}
                        className={`bg-[#0a0a0a] rounded p-2 border cursor-pointer transition-colors overflow-hidden ${isToday ? 'border-[#15803d]' : isSelected ? 'border-[#3a3a3a]' : hasUserEvent ? 'border-[#1a1a1a]' : 'border-transparent'} hover:border-[#2a2a2a]`}
                      >
                        <div className={`text-xs font-semibold mb-1.5 flex-shrink-0 ${isToday ? 'text-[#15803d]' : 'text-gray-400'}`}>{day}</div>
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, MAX_VISIBLE).map((event, idx) => (
                            <div key={idx} className={`text-xs px-1.5 py-0.5 rounded ${getEventStyles(event)}`}>
                              <span className="font-mono font-bold">{event.ticker}</span>
                              <span className="ml-1 opacity-70">{event.label}</span>
                            </div>
                          ))}
                          {overflow > 0 && <div className="text-xs text-gray-500 px-1">+{overflow} more</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>

              {/* Day detail popup — full-width overlay at bottom of modal */}
              {selectedCalDay !== null && (() => {
                const allPopEvents = calendarEvents.filter(e => {
                  const d = adjustWeekend(e.date, e.type);
                  if (!d) return false;
                  return d.getFullYear() === year && d.getMonth() === month && d.getDate() === selectedCalDay;
                }).sort((a, b) => {
                  if (calendarSort === 'yours') {
                    if (a.isUserPosition && !b.isUserPosition) return -1;
                    if (!a.isUserPosition && b.isUserPosition) return 1;
                    return a.ticker.localeCompare(b.ticker);
                  } else if (calendarSort === 'alpha') {
                    return a.ticker.localeCompare(b.ticker);
                  } else {
                    const typeOrder = { dividend: 0, payment: 1, earnings: 2 };
                    return (typeOrder[a.type] ?? 3) - (typeOrder[b.type] ?? 3);
                  }
                });
                const popEvents = calendarPopupSearch
                  ? allPopEvents.filter(e => e.ticker.includes(calendarPopupSearch))
                  : allPopEvents;
                if (allPopEvents.length === 0) return null;
                const dateLabel = new Date(year, month, selectedCalDay).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                const exDivCount = allPopEvents.filter(e => e.type === 'dividend').length;
                const payCount = allPopEvents.filter(e => e.type === 'payment').length;
                const earningsCount = allPopEvents.filter(e => e.type === 'earnings').length;
                return (
                  <div className="absolute inset-x-0 bottom-0 z-20 bg-[#0d0d0d] border-t border-[#2a2a2a] shadow-2xl flex flex-col" style={{ height: '52%' }}>
                    {/* Popup header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b border-[#1a1a1a] flex-shrink-0">
                      <div className="flex items-center gap-4">
                        <div>
                          <span className="text-sm font-bold text-white">{dateLabel}</span>
                          <span className="ml-2 text-xs text-gray-500">{allPopEvents.length} event{allPopEvents.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          {exDivCount > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-amber-400 font-semibold">{exDivCount}</span><span className="text-gray-500">Ex-Div</span></span>}
                          {payCount > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /><span className="text-emerald-400 font-semibold">{payCount}</span><span className="text-gray-500">Pay Date</span></span>}
                          {earningsCount > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" /><span className="text-purple-400 font-semibold">{earningsCount}</span><span className="text-gray-500">Earnings</span></span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <input
                            type="text"
                            value={calendarPopupSearch}
                            onChange={e => setCalendarPopupSearch(e.target.value.toUpperCase())}
                            placeholder="Filter ticker…"
                            className="bg-[#1a1a1a] border border-[#2a2a2a] rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#3a3a3a] w-36"
                          />
                          {calendarPopupSearch && (
                            <button onClick={() => setCalendarPopupSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs">✕</button>
                          )}
                        </div>
                        <button onClick={() => { setSelectedCalDay(null); setCalendarPopupSearch(''); }} className="text-gray-500 hover:text-white p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Event list */}
                    <div className="overflow-y-auto flex-1 p-3">
                      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                        {popEvents.map((event, idx) => {
                          const { color } = getEventTypeLabel(event.type);
                          return (
                            <div key={idx} className={`flex items-center justify-between px-3 py-2.5 rounded ${getEventStyles(event)}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getEventDot(event.type)}`} />
                                {event.isUserPosition && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/50 font-bold uppercase tracking-wide flex-shrink-0">★</span>
                                )}
                                <span className="font-mono font-bold text-sm">{event.ticker}</span>
                                <span className={`text-xs font-semibold ${color}`}>{event.label}</span>
                                {event.amount > 0 && <span className="text-xs text-gray-500">${event.amount.toFixed(4)}</span>}
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => downloadICS(event)} className="text-xs opacity-40 hover:opacity-100 px-1.5 py-1 hover:bg-black/20 rounded transition-opacity">📥</button>
                                <button onClick={() => openGoogleCalendar(event)} className="text-xs opacity-40 hover:opacity-100 px-1.5 py-1 hover:bg-black/20 rounded transition-opacity">📅</button>
                              </div>
                            </div>
                          );
                        })}
                        {popEvents.length === 0 && calendarPopupSearch && (
                          <div className="col-span-3 text-center text-xs text-gray-600 py-4">No matches for "{calendarPopupSearch}"</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default TickerTap;
